import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { EuiFlexGroup, EuiFlexItem, EuiSpacer } from "@elastic/eui";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import TopFilter from "components/TopFilter/TopFilter";
import AlertsService from "services/AdminConsole/Monitoring/Profiles/MonitoringProfilesService";
import { NEGATE_FILTER } from "Consts";
import Button from "components/Buttons/Button";
import ToggleButtons from "components/Buttons/ToggleButtons";
import ProfileAlertsList from "./ProfileAlertsList";
import ProfileAlertsListAdvanced from "./ProfileAlertsListAdvanced";
import { AlertType } from "../../profileTypes";
import { useNewProfilieAlertsAssignStyles } from "../../profileStyles";
import { ALL_OBJECTS_LABEL } from "../../profileConsts";
import SelectablePopover, { SelectablePopoverOption } from "components/Popover/SelectablePopover";

interface Props {
  selectedAlertTypes: AlertType[];
  setSelectedAlertTypes: (types: AlertType[]) => void;
  setIsDirty: (isDirty: boolean) => void;
  isSystemProfile?: boolean;
  monitoringProfileId: number;
}

type CustomSelectableOption = SelectablePopoverOption & {
  key: string;
  label: string;
  checked?: "on" | undefined;
  severityTypeName?: string;
};

const filters = [
  {
    field: "alertTypeSeverity",
    name: "Severity",
    options: [{ value: "Critical" }, { value: "Warning" }, { value: "Informational" }],
  },
];

const ProfileAlertsAssign = ({
  selectedAlertTypes,
  setSelectedAlertTypes,
  setIsDirty,
  isSystemProfile,
  monitoringProfileId,
}: Props) => {
  const { GlobalTopFilterObject } = useSelector(globalFilterSelector, shallowEqual);
  const classes = useNewProfilieAlertsAssignStyles();
  const { clientId } = useSelector(globalFilterSelector, shallowEqual);
  const [availableAlertTypes, setAvailableAlertTypes] = useState<AlertType[]>([]);
  const [isPopoverOpen, setPopoverOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const [options, setOptions] = useState<CustomSelectableOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const hasDuplicateAlertTypes = useMemo(() => {
    const seen = new Set<string>();
    return selectedAlertTypes.some((alert) => {
      const key = `${alert.id}-${alert.subtypeId ?? "null"}`;
      if (seen.has(key)) {
        return true;
      }
      seen.add(key);
      return false;
    });
  }, [selectedAlertTypes]);

  const hasObjectAssignments = useMemo(() => {
    return selectedAlertTypes.some(
      (alert) =>
        alert.selectedObjectNames &&
        alert.selectedObjectNames.length > 0 &&
        alert.selectedObjectNames.some((name) => name !== ALL_OBJECTS_LABEL)
    );
  }, [selectedAlertTypes]);

  const shouldForceAdvancedMode = hasDuplicateAlertTypes || hasObjectAssignments;

  const [mode, setMode] = useState<"basic" | "advanced">(() =>
    shouldForceAdvancedMode ? "advanced" : "basic"
  );

  useEffect(() => {
    if (shouldForceAdvancedMode && mode === "basic") {
      setMode("advanced");
    }
  }, [shouldForceAdvancedMode, mode]);

  const getAvailableAlertTypes = async () => {
    setIsLoading(true);
    try {
      const result = await AlertsService.getAvailableAlertTypes({
        clientId,
      });
      if (result?.alertTypes) {
        setAvailableAlertTypes(result.alertTypes);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAvailableAlertTypes = useMemo(
    () =>
      availableAlertTypes.filter((type) =>
        !GlobalTopFilterObject.alertTypeSeverity?.length
          ? true
          : GlobalTopFilterObject.alertTypeSeverity.some((severityFilter) => {
              const isExclude = severityFilter.startsWith(NEGATE_FILTER);
              const severity = isExclude
                ? severityFilter.replace(NEGATE_FILTER, "")
                : severityFilter;
              return (type.severityTypeName === severity) !== isExclude;
            })
      ),
    [availableAlertTypes, GlobalTopFilterObject.alertTypeSeverity]
  );

  useEffect(() => {
    getAvailableAlertTypes();
  }, [clientId]);

  // Effect 1: Options from filtered types
  useEffect(() => {
    if (!Array.isArray(filteredAvailableAlertTypes)) return;
    setOptions(
      filteredAvailableAlertTypes.map((type) => ({
        label: type.name,
        key: `${type.id.toString()}-${
          type.subtypeId !== undefined && type.subtypeId !== null
            ? type.subtypeId.toString()
            : "null"
        }`,
        checked: undefined,
        secondaryValue: type.severityTypeName || "",
        severityTypeName: type.severityTypeName,
      }))
    );
  }, [filteredAvailableAlertTypes]);

  // Effect 2: Selection sync
  useEffect(() => {
    if (!isPopoverOpen) return;

    setOptions((prevOptions) =>
      prevOptions.map((option) => {
        const [id, subtypeId] = option.key.split("-");
        const parsedId = parseInt(id, 10);
        const parsedSubtypeId = subtypeId === "null" ? null : parseInt(subtypeId, 10);

        const matchingAlert = selectedAlertTypes.find(
          (alert) =>
            alert.id === parsedId &&
            (alert.subtypeId === parsedSubtypeId ||
              (alert.subtypeId === null && subtypeId === "null"))
        );
        return {
          ...option,
          checked: matchingAlert ? "on" : undefined,
        };
      })
    );
  }, [isPopoverOpen, selectedAlertTypes]);

  // Effect 3: Select all state
  useEffect(() => {
    setSelectAll(options.length > 0 && options.length === selectedAlertTypes.length);
  }, [options.length, selectedAlertTypes.length]);

  const onButtonClick = () => {
    if (isSystemProfile) return;
    setPopoverOpen(!isPopoverOpen);
  };

  const closePopover = () => {
    setPopoverOpen(false);
  };

  const handleApplyResources = () => {
    // Get currently visible checked options
    const visibleChecked = options.filter(
      (o): o is CustomSelectableOption & { key: string } =>
        o.checked === "on" && typeof o.key === "string"
    );

    // Keep previously selected items that are currently filtered out
    const filteredOutSelected = selectedAlertTypes.filter((alert) => {
      const alertKey = `${alert.id}-${
        alert.subtypeId !== undefined && alert.subtypeId !== null
          ? alert.subtypeId.toString()
          : "null"
      }`;
      return !options.some((opt) => opt.key === alertKey);
    });

    const updatedTypes = [...filteredOutSelected];

    // Process visible checked options
    visibleChecked.forEach((checkedOption) => {
      const [id, subtypeId] = checkedOption.key.split("-");
      const parsedId = parseInt(id, 10);
      const parsedSubtypeId = subtypeId === "null" ? null : parseInt(subtypeId, 10);

      const type = availableAlertTypes.find((t) => {
        const idMatch = t.id === parsedId;
        const subtypeMatch = t.subtypeId === parsedSubtypeId;
        return idMatch && subtypeMatch;
      });

      if (!type?.id || !type?.name || !type?.formula) {
        console.warn(`Missing required fields for alert type ${checkedOption.key}`);
        return;
      }

      // Check if this alert type group already exists in selectedAlertTypes
      const existingAlertGroup = selectedAlertTypes.filter(
        (existing) => existing.id === type.id && existing.subtypeId === type.subtypeId
      );

      // If the group already exists, keep all existing levels
      if (existingAlertGroup.length > 0) {
        // Add all existing levels for this alert type to updatedTypes
        existingAlertGroup.forEach((existingAlert) => {
          const index = updatedTypes.findIndex(
            (existing) =>
              existing.id === existingAlert.id &&
              existing.subtypeId === existingAlert.subtypeId &&
              existing.alertLevel === existingAlert.alertLevel
          );

          if (index > -1) {
            updatedTypes[index] = {
              ...updatedTypes[index],
              checked: "on" as const,
              thresholds: type.thresholds || [],
            };
          } else {
            updatedTypes.push({
              ...existingAlert,
              checked: "on" as const,
              thresholds: type.thresholds || [],
            });
          }
        });
      } else {
        // Only create a new level 1 alert if this is a completely new alert type
        const defaultThresholdId = type.thresholds?.[0]?.id || 0;

        const newAlertType = {
          id: type.id,
          name: type.name,
          formula: type.formula,
          thresholdId: defaultThresholdId,
          severityTypeId: type.severityTypeId || 1,
          subtypeId: type.subtypeId,
          severityTypeName: type.severityTypeName || "",
          checked: "on" as const,
          tableChecked: undefined,
          thresholds: type.thresholds || [],
          alertLevel: 1, // Always start with level 1 for new alert types
          objectAssignments: type.objectAssignments || [],
          selectedObjectNames: [],
          objectName: type.objectName,
        };
        updatedTypes.push(newAlertType);
      }
    });

    setSelectedAlertTypes(updatedTypes);
    setIsDirty(true);
    closePopover();
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isSystemProfile) return;
    const isChecked = e.target.checked;
    setSelectAll(isChecked);

    const updatedOptions = options.map((option) => ({
      ...option,
      checked: isChecked ? ("on" as const) : undefined,
    }));
    setOptions(updatedOptions);
    setIsDirty(true);
  };

  const handleSelectableOptionsChange = (newOptions: SelectablePopoverOption[]) => {
    if (isSystemProfile) return;
    const updatedOptions = newOptions.map((newOpt) => {
      const existingOpt = options.find((opt) => opt.key === newOpt.key);
      if (!existingOpt) return newOpt as CustomSelectableOption;

      const [id, subtypeId] = existingOpt.key.split("-");

      return {
        ...newOpt,
        key: `${id}-${subtypeId || ""}`,
        checked: newOpt.checked,
        severityTypeName: existingOpt.severityTypeName,
      } as CustomSelectableOption;
    });
    setOptions(updatedOptions);
    setIsDirty(true);
    setSelectAll(updatedOptions.every((opt) => opt.checked === "on"));
  };

  const handleClearSelection = () => {
    if (isSystemProfile) return;

    const updatedAlertTypes = selectedAlertTypes.filter((alert) => alert.tableChecked !== "on");
    setSelectedAlertTypes(updatedAlertTypes);
    setIsDirty(true);
  };

  const selectableOptions: SelectablePopoverOption[] = options
    .map((option) => ({
      ...option,
      secondaryValue: option.severityTypeName || "",
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <div>
      <EuiFlexGroup justifyContent="spaceBetween" alignItems="center">
        <EuiFlexItem grow={false}>
          <TopFilter Filters={filters} />
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <ToggleButtons
            labels={["Basic Mode", "Advanced Mode"]}
            defaultSelectedIndex={mode === "advanced" ? 1 : 0}
            disabled={[shouldForceAdvancedMode, false]}
            onChange={(index) => {
              const newMode = index === 0 ? "basic" : "advanced";
              setMode(newMode);
            }}
          />
        </EuiFlexItem>
      </EuiFlexGroup>

      <div className={classes.controlContainer}>
        <EuiFlexGroup
          className={classes.fullWidth}
          alignItems="flexEnd"
          justifyContent="spaceBetween">
          <EuiFlexItem grow={false} style={{ width: "50%" }}>
            <SelectablePopover
              isOpen={isPopoverOpen}
              onClose={closePopover}
              onButtonClick={onButtonClick}
              onApply={handleApplyResources}
              options={selectableOptions}
              onOptionsChange={handleSelectableOptionsChange}
              selectAll={selectAll}
              onSelectAllChange={handleSelectAll}
              buttonText="Select Alert Type"
              searchPlaceholder="Search alert types..."
              columnHeaders={["Alert Type", "Default Severity"]}
              isLoading={isLoading}
              disabled={isSystemProfile}
              className={classes.selectButton}
            />
          </EuiFlexItem>
          <EuiFlexItem grow={false} onClick={handleClearSelection}>
            <Button
              text="Bulk Delete"
              type="primary"
              disabled={
                isSystemProfile ||
                selectedAlertTypes.length === 0 ||
                selectedAlertTypes.every((alert) => alert.tableChecked !== "on")
              }
              onClick={handleClearSelection}
            />
          </EuiFlexItem>
        </EuiFlexGroup>
      </div>
      <EuiSpacer size="m" />
      {mode === "basic" && (
        <ProfileAlertsList
          selectedAlerts={selectedAlertTypes}
          setSelectedAlerts={setSelectedAlertTypes}
          setIsDirty={setIsDirty}
          availableAlertTypes={availableAlertTypes}
          isSystemProfile={isSystemProfile}
        />
      )}
      {mode === "advanced" && (
        <ProfileAlertsListAdvanced
          selectedAlerts={selectedAlertTypes}
          setSelectedAlerts={setSelectedAlertTypes}
          setIsDirty={setIsDirty}
          availableAlertTypes={availableAlertTypes}
          isSystemProfile={isSystemProfile}
          monitoringProfileId={monitoringProfileId}
        />
      )}
    </div>
  );
};

export default ProfileAlertsAssign;
